import { useMemo } from 'react';
import { WorkflowDto, WorkflowStepDto } from 'src/api/FiretrolApiClient';

export interface IWorkflowAuthProps {
  allowedRoles: number[];
  allowedUsers: (number | undefined)[];
  blockedUsers: (number | undefined)[];
  allowOwner: boolean;
}

export interface IWorkflowState {
  currentStep: WorkflowStepDto | null;
  nextStep: WorkflowStepDto | null;
  isCreating: boolean;
  isNextStepBatchHandled: boolean;
  authProps: IWorkflowAuthProps;
}

/** @registry Derives the current/next step, creating flag, batch-handling flag, and authorization props for WorkflowButtons. */
export function useWorkflowState(
  workflow: WorkflowDto | null,
  stepIndex: number,
): IWorkflowState {
  return useMemo(() => {
    if (!workflow) {
      return {
        currentStep: null,
        nextStep: null,
        isCreating: true,
        isNextStepBatchHandled: false,
        authProps: {
          allowedRoles: [],
          allowedUsers: [],
          blockedUsers: [],
          allowOwner: false,
        },
      };
    }

    const currentStep = stepIndex >= 0 ? workflow.steps[stepIndex] : null;
    const nextStep = workflow.steps[stepIndex + 1] ?? null;
    const isCreating = currentStep === null || stepIndex === -1;

    const isNextStepBatchHandled =
      nextStep?.isEmployeeReviewStep ||
      nextStep?.isSupervisorReviewStep ||
      false;

    // Derive authorization props from current step or workflow defaults
    const roles =
      stepIndex >= 0
        ? currentStep?.assignedRoles ?? []
        : workflow.authorizedRoles;
    const users =
      stepIndex >= 0
        ? currentStep?.assignedUsers ?? []
        : workflow.authorizedUsers;
    const excluded =
      stepIndex >= 0
        ? currentStep?.excludedAssignedUsers ?? []
        : workflow.excludedUsers;

    const authProps: IWorkflowAuthProps = {
      allowedRoles: roles.map((r) => r.organizationRoleId ?? -1),
      allowedUsers: users.map((u) => u.jobUserId),
      blockedUsers: excluded.map((u) => u.jobUserId),
      allowOwner:
        (stepIndex >= 0 ? currentStep?.allowOwner : workflow.allowOwner) ??
        false,
    };

    return {
      currentStep,
      nextStep,
      isCreating,
      isNextStepBatchHandled,
      authProps,
    };
  }, [workflow, stepIndex]);
}
